import { type UseModalState, useModalState } from "@clipboard-health/ui-react";
import { ProgressDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useRef, useState } from "react";

import { type HcfTimeclockComplianceVideoConfig } from "../ShiftState/types";
import { ClockActionPictureDialogEvent } from "./ClockActionPictureDialogEvent";
import { TimeclockCameraAccessDialog } from "./dialogs/CameraAccessDialog";
import { NfcSuccessDialog } from "./dialogs/NfcSuccessDialog";
import { RecordVideoDialog } from "./dialogs/RecordVideoDialog";
import { TimeclockComplianceVideoPolicyDialog } from "./dialogs/VideoPolicyDialog";
import { VideoUploadErrorDialog } from "./dialogs/VideoUploadErrorDialog";
import { getPermissions } from "./utils";

const environment = process.env.REACT_APP_ENVIRONMENT_NAME;

const S3_BUCKET_URL = "https://facility-timeclock-compliance-experiment-images.s3.amazonaws.com";

export interface TimeclockComplianceVideoDialogProps {
  modalState: UseModalState;
  isNfcEnabled: boolean;
  facilityName: string;
  timeclockLocation: string;
  timeclockPin: string | undefined;
  workplaceId: string;
  shiftId: string;
  workerId: string;
  clockAction: "clock-in" | "clock-out";
  videoConfig: HcfTimeclockComplianceVideoConfig | undefined;

  onSuccess: () => void;
  onCancel: () => void;
}

function generateFileName(props: {
  workplaceId: string;
  workerId: string;
  shiftId: string;
  clockAction: "clock-in" | "clock-out";
}) {
  const { workplaceId, workerId, shiftId, clockAction } = props;
  const hourMinuteSeconds = new Date().toISOString().slice(11, 19);
  const fileName = `videos/${workplaceId}/w-${workerId}-s-${shiftId}-${clockAction}-${hourMinuteSeconds}.mp4`;

  return environment ? `${environment}/${fileName}` : `${fileName}`;
}

async function uploadVideoToS3(
  videoBlob: Blob,
  uploadFileName: string,
  logData: Record<string, unknown>
): Promise<{ isSuccess: boolean }> {
  const uploadStartTime = Date.now();

  try {
    const formData = new FormData();
    formData.append("key", uploadFileName);
    formData.append("file", videoBlob);

    /*
      Making a "no-cors" request to S3 bucket because the bucket
      is returning a 307 redirect without CORS headers at the moment.
      Since the request is "no-cors", it returns an opaque response
      which doesn't have status code, so we don't know if the upload
      was successful and we're not handling errors.
    */
    await fetch(S3_BUCKET_URL, {
      method: "POST",
      body: formData,
      mode: "no-cors",
    });

    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
      ...logData,
      event: ClockActionPictureDialogEvent.UPLOAD_SUCCESS,
      video: true,
      uploadDurationMs: Date.now() - uploadStartTime,
      mediaType: "video",
      mediaUrl: `${S3_BUCKET_URL}/${uploadFileName}`,
      mediaSize: videoBlob.size,
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";

    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
      ...logData,
      event: ClockActionPictureDialogEvent.UPLOAD_FAILURE,
      video: true,
      error: errorMessage,
    });

    return { isSuccess: false };
  }

  return { isSuccess: true };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function TimeclockComplianceVideoDialog(props: TimeclockComplianceVideoDialogProps) {
  const {
    modalState,
    isNfcEnabled,
    facilityName = "The facility",
    timeclockLocation,
    timeclockPin,
    workplaceId,
    shiftId,
    workerId,
    clockAction,
    videoConfig,
    onSuccess,
    onCancel,
  } = props;

  const [currentPage, setCurrentPage] = useState<
    "policy" | "record-video" | "nfc-success-dialog" | "error"
  >("policy");
  const [videoErrorText, setVideoErrorText] = useState<string>();
  const videoBlobRef = useRef<Blob>();
  const [isUploading, setIsUploading] = useState(false);
  const camereAccessModalState = useModalState();

  const logData = {
    isNfcEnabled,
    action: clockAction,
    shiftId,
    workerId,
    workplaceId,
    timeclockLocation,
    pinLength: timeclockPin ? timeclockPin.length : undefined,
  };

  const continueToRecording = async () => {
    const { camera, microphone } = await getPermissions();

    if (!camera || !microphone) {
      camereAccessModalState.openModal();
      return;
    }

    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
      ...logData,
      event: ClockActionPictureDialogEvent.POLICY_ACCEPTED,
      video: true,
    });
    setCurrentPage("record-video");
  };

  const onUploadSettled = (isSuccess: boolean) => {
    if (!isSuccess) {
      setCurrentPage("error");
      return;
    }

    if (isNfcEnabled) {
      setCurrentPage("nfc-success-dialog");
    } else {
      onSuccess();
    }
  };

  const startVideoUpload = async () => {
    const videoBlob = videoBlobRef.current;

    if (!videoBlob) {
      logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
        ...logData,
        event: ClockActionPictureDialogEvent.VIDEO_RECORDING_FAILED,
        video: true,
        error: "Missing videoBlob at upload",
        hasVideoBlob: false,
      });
      setVideoErrorText("There was an error with the video. Please try again.");
      onUploadSettled(false);
      return;
    }

    setIsUploading(true);

    const uploadFileName = generateFileName({ workplaceId, workerId, shiftId, clockAction });
    const { isSuccess } = await uploadVideoToS3(videoBlob, uploadFileName, logData);

    setIsUploading(false);

    if (!isSuccess) {
      setVideoErrorText(
        "Something went wrong. Please ensure you have reliable connectivity and retry upload. It may help to connect to the facility's Wi-Fi."
      );
    }

    onUploadSettled(isSuccess);
  };

  const onRetryVideoUpload = () => {
    if (videoBlobRef.current !== undefined && videoBlobRef.current.size > 0) {
      void startVideoUpload();
      logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EXPERIMENT_EVENT, {
        ...logData,
        event: ClockActionPictureDialogEvent.REUPLOAD_VIDEO,
        video: true,
      });
      return;
    }

    setCurrentPage("record-video");
    videoBlobRef.current = undefined;
    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EXPERIMENT_EVENT, {
      ...logData,
      event: ClockActionPictureDialogEvent.RETAKE_VIDEO,
      video: true,
    });
  };

  return modalState.modalIsOpen ? (
    <>
      {currentPage === "policy" && (
        <TimeclockComplianceVideoPolicyDialog
          facilityName={facilityName}
          clockAction={clockAction}
          timeclockLocation={timeclockLocation}
          timeclockPin={timeclockPin}
          onContinue={continueToRecording}
          onCancel={() => {
            onCancel();

            logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
              ...logData,
              event: ClockActionPictureDialogEvent.POLICY_CANCELLED,
              video: true,
            });
          }}
        />
      )}
      {currentPage === "record-video" && (
        <RecordVideoDialog
          videoConfig={videoConfig}
          logData={logData}
          videoConfirmedCallback={(blob) => {
            videoBlobRef.current = blob;
            void startVideoUpload();
          }}
          onClose={() => {
            videoBlobRef.current = undefined;
            setCurrentPage("policy");
          }}
        />
      )}
      {currentPage === "nfc-success-dialog" && <NfcSuccessDialog isVideo onContinue={onSuccess} />}
      {currentPage === "error" && (
        <VideoUploadErrorDialog
          errorText={videoErrorText ?? "Some unexpected error occured while submitting the video."}
          isLoading={isUploading}
          canRetryUpload={videoBlobRef.current !== undefined}
          onRetry={onRetryVideoUpload}
          onClose={onCancel}
        />
      )}
      <TimeclockCameraAccessDialog
        modalState={camereAccessModalState}
        onContinue={camereAccessModalState.closeModal}
        onCancel={camereAccessModalState.closeModal}
      />
      <ProgressDialog modalIsOpen={isUploading} messageContent="Uploading video" />
    </>
  ) : null;
}
